import type { InputProps } from "@cloudscape-design/components/input"
import type { TextareaProps } from "@cloudscape-design/components/textarea"
import Box from "@cloudscape-design/components/box"
import Input from "@cloudscape-design/components/input"
import Textarea from "@cloudscape-design/components/textarea"
import { useState, useRef, useEffect } from "react"

interface InlineEditableTextProps {
  /**
   * Current text value to display.
   */
  value: string
  /**
   * Callback when text is saved (blur or Enter)
   */
  onSave: (newValue: string) => void
  /**
   * Callback when editing is cancelled (Escape)
   */
  onCancel?: () => void
  /**
   * Whether the component is currently in edit mode.
   */
  editing?: boolean
  /**
   * Callback to toggle edit mode externally.
   */
  onEditingChange?: (editing: boolean) => void
  /**
   * CSS class name for the display text.
   */
  className?: string
  /**
   * CSS class name for the input element.
   */
  inputClassName?: string
  /**
   * When true, edits with a multi-line Textarea instead of a single-line Input.
   */
  multiline?: boolean
}

/**
 * Inline editable text component with click-to-edit behavior.
 *
 * Displays text that becomes an input field when clicked. Saves on blur or Enter key, cancels on
 * Escape. Used for period and category names in the assessments grid.
 *
 * Built from Cloudscape Input and Box primitives because Cloudscape does not provide a standalone
 * InlineEditor component (inline editing is only available as a Table column feature).
 */
const InlineEditableText = ({
  value,
  onSave,
  onCancel,
  editing: controlledEditing,
  onEditingChange,
  className = "",
  inputClassName = "",
  multiline = false,
}: InlineEditableTextProps) => {
  const [internalEditing, setInternalEditing] = useState(false)
  const [inputValue, setInputValue] = useState(value)
  const inputRef = useRef<InputProps.Ref>(null)
  const textareaRef = useRef<TextareaProps.Ref>(null)

  const isEditing = controlledEditing ?? internalEditing

  const setEditing = (val: boolean) => {
    if (onEditingChange) {
      onEditingChange(val)
    } else {
      setInternalEditing(val)
    }
  }

  useEffect(() => {
    if (isEditing) {
      setInputValue(value)
      // Defer focus to next tick so the Input is mounted
      setTimeout(() => {
        if (multiline) {
          textareaRef.current?.focus()
        } else if (inputRef.current) {
          inputRef.current.focus()
          inputRef.current.select()
        }
      }, 0)
    }
  }, [isEditing, value, multiline])

  const handleSave = (newValue: string) => {
    if (newValue.trim() && newValue.trim() !== value) {
      onSave(newValue.trim())
    }
    setEditing(false)
  }

  const handleCancel = () => {
    onCancel?.()
    setEditing(false)
  }

  if (isEditing) {
    return (
      <Box display="inline-block" className={inputClassName}>
        {multiline ? (
          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={({ detail }) => setInputValue(detail.value)}
            onBlur={() => handleSave(inputValue)}
            onKeyDown={(event) => {
              if (event.detail.key === "Enter") {
                event.preventDefault()
                handleSave(inputValue)
              }
              if (event.detail.key === "Escape") handleCancel()
            }}
            rows={1}
            autoFocus
          />
        ) : (
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={({ detail }) => setInputValue(detail.value)}
            onBlur={() => handleSave(inputValue)}
            onKeyDown={({ detail }) => {
              if (detail.key === "Enter") handleSave(inputValue)
              if (detail.key === "Escape") handleCancel()
            }}
            autoFocus
          />
        )}
      </Box>
    )
  }

  return (
    <Box display="inline-block">
      {/* cursor: pointer is necessary because Cloudscape has no "clickable text" primitive */}
      <Box variant="span" className={className}>
        <span onClick={() => setEditing(true)} style={{ cursor: "pointer" }}>
          {value}
        </span>
      </Box>
    </Box>
  )
}

export default InlineEditableText
